import { authFetch } from '@/utils/auth-fetch';

/** Google bucket delivery — client for backend-merged/routes/bucket-delivery.js. */

/** 'preparing' (phase 2.1, #7015): a client destination stored by Prepare, not checked before Check and enable. */
export type DeliveryStatus = 'not_enabled' | 'provisioning' | 'provisioning_failed' | 'preparing' | 'enabled' | 'disabled';
export type AccountType = 'user' | 'serviceAccount' | 'group';
export type IamStatus = 'pending_add' | 'applied' | 'pending_remove' | 'error';
export type RunStatus = 'queued' | 'running' | 'failed_retryable' | 'completed' | 'failed';

export interface DeliveryAccount {
  principal: string;
  type: AccountType;
  email: string;
  iamStatus: IamStatus;
  iamError: string | null;
  addedAt: string;
}

/** The publication guard's outcome summed over a full sync (the API's governance headers, per run). */
export interface DeliveryGovernance {
  /** products delivered without their generated content: refused in every language decided for them */
  refused: number;
  /** products that had generated content to decide on */
  withStoredGeneration: number;
  /** products delivered with some languages' generated content withheld */
  partialLanguageProducts: number;
  /** refused products per refusal condition */
  conditions: Record<string, number>;
}

export interface DeliveryRun {
  id: string;
  status: RunStatus;
  folder: string;
  requestedBy: 'schedule' | 'user';
  products: number;
  files: number;
  bytesGzip: number;
  governance: DeliveryGovernance;
  attempts: number;
  error: string | null;
  baseline: string | null;
  createdAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  nextAttemptAt: string | null;
  filesDeleted: boolean;
  manifestUrl?: string | null;
  folderUrl?: string | null;
}

export interface DeliveryLimits {
  maxAccounts: number;
  /** phase 2.2: the minimum time between two requested full exports (a failed or superseded one does not count) */
  fullSyncMinIntervalHours: number;
  productsPerFile: number;
  disableGraceDays: number;
  previousFullGraceHours: number;
}

/** Phase 2 (#6988): where files and deltas go. */
export type DestinationMode = 'hosted' | 'client';

export interface ClientGrant {
  role: string;
  resource: string;
  member: string;
  /** phase 2.4: the ready `gcloud` command for this grant, account and resource filled in by the server */
  command: string;
  /** phase 2.4: where the same grant is made in the Cloud console, e.g. "Cloud Storage › Buckets › b › Permissions › Grant access" */
  consolePath: string;
}

/** Client-owned destination: the client's bucket (and topic) in its own Google Cloud project. */
export interface ClientDestination {
  projectId: string;
  bucket: string;
  prefix: string;
  topic: string | null;
  verifiedAt: string | null;
  checkedAt: string | null;
  /** phase 2.1: when Prepare stored this destination */
  preparedAt?: string | null;
  /** phase 2.1: Check now was clicked; the worker checks at its next tick (within 15 s) */
  checkRequestedAt?: string | null;
  /** phase 2.1: the worker's next automatic check, while a check is due (null: at its next pass) */
  nextCheckAt?: string | null;
  /** bucket side: probe, file writes (Google's message, verbatim) */
  error: string | null;
  /** phase 2.1: the likely cause of `error` in plain words, shown before it (null when none applies) */
  errorSummary?: string | null;
  /** topic side: the topic check or the last publishing error; a publishing error is cleared only by a successful publish or a topic change */
  topicError?: string | null;
  topicVerifiedAt?: string | null;
  /** phase 2.3: a refusal of the store's account right after Check and enable: most likely a grant Google has not applied yet ("checking") */
  propagating?: boolean;
  /**
   * Phase 2.3 (#7077): this store's own Google service account, the only identity its calls run as and the
   * only one the client grants. Null while it is being created, and after it was deleted.
   */
  serviceAccount: string | null;
  serviceAccountState?: ServiceAccountState | null;
  serviceAccountCreatedAt?: string | null;
  /** why creating the account failed (retried by the worker every 5 minutes) */
  serviceAccountError?: string | null;
  /** the account released for deletion (deletion pending, refused, or deleted), named for the tab */
  releasedServiceAccount?: string | null;
  /** why its deletion was refused (ownership not confirmed by Google): the account may still exist */
  serviceAccountDeletionRefused?: string | null;
  /** the roles to grant, to `serviceAccount`; empty until it exists */
  grants: ClientGrant[];
}

/**
 * Phase 2.3: 'creating' after Prepare until the worker has created it; released 14 days after turning off:
 * 'deletion_pending' until Google confirms the deletion ('deleted'), or 'deletion_refused' (PR #7078 round 6).
 */
export type ServiceAccountState = 'creating' | 'ready' | 'deletion_pending' | 'deletion_refused' | 'deleted';

export interface DeliveryLayout {
  fullFolder: string;
  part: string;
  manifest: string;
  staging: string;
}

export interface DeltaRun {
  id: string;
  status: RunStatus;
  changedSince: string | null;
  windowHigh: string | null;
  runSeq: number | null;
  topic: string | null;
  messagesPublished: number;
  governance: DeliveryGovernance;
  attempts: number;
  error: string | null;
  createdAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  nextAttemptAt: string | null;
}

export interface DeliverySubscription {
  name: string;
  topic: string;
  detachPending: boolean;
}

export interface DeliveryPubSub {
  deltasFlowing: boolean;
  /** hosted: our files topic (object-finalize notifications on full/); null in client mode */
  filesTopic: string | null;
  /** hosted: our deltas topic; client: the client's topic */
  topic: string | null;
  topicsReadyAt: string | null;
  publishWatermark: string | null;
  runSeq: number;
  lastDelta: DeltaRun | null;
  activeDelta: DeltaRun | null;
  nextDeltaAt: string | null;
  deltaIntervalMinutes: number;
  topicRetentionDays: number;
  /** hosted: the time a late subscriber seeks to */
  seekFrom: string | null;
  subscriptions: DeliverySubscription[] | null;
  subscriptionsRefreshedAt: string | null;
  pendingDetaches: { subscription: string; topic: string; requestedAt: string }[];
}

/** POST /enable body: a PageMind-hosted bucket (phase 2.1: a client destination is prepared instead). */
export type EnableRequest = { mode: 'hosted' };

/** POST /prepare body (phase 2.1, #7015): step 1 of a destination in the client's own project. */
export type PrepareRequest = { mode: 'client'; project_id: string; bucket: string; prefix?: string; topic?: string | null };

export interface BucketDelivery {
  status: DeliveryStatus;
  shapeVersion: number;
  /** absent from a phase 1 backend: read as 'hosted' */
  destinationMode?: DestinationMode;
  client?: ClientDestination | null;
  layout?: DeliveryLayout;
  pubsub?: DeliveryPubSub;
  bucket?: string | null;
  bucketUrl?: string | null;
  location?: string | null;
  lastError?: string | null;
  enabledAt?: string | null;
  provisioningSince?: string | null;
  disabledAt?: string | null;
  bucketDeletionAt?: string | null;
  bucketDeletedAt?: string | null;
  /** retention has started deleting the bucket; enabling again is refused until it is done */
  bucketDeletionStartedAt?: string | null;
  /** phase 2.2: full exports run on request only; this is when the next request is accepted (null: now) */
  fullSyncAllowedAt?: string | null;
  accounts?: DeliveryAccount[];
  latestFull?: DeliveryRun | null;
  activeRun?: DeliveryRun | null;
  lastFinishedRun?: DeliveryRun | null;
  limits: DeliveryLimits;
}

export class DeliveryApiError extends Error {
  status: number;
  code: string | null;

  constructor(status: number, message: string, code: string | null) {
    super(message);
    this.name = 'DeliveryApiError';
    this.status = status;
    this.code = code;
  }
}

async function call(path: string, init: RequestInit = {}, signal?: AbortSignal): Promise<{ delivery: BucketDelivery }> {
  const response = await authFetch(path, { ...init, signal });
  let body: any = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  if (!response.ok) {
    const message = (body && typeof body.error === 'string' && body.error) || `Request failed with HTTP ${response.status}`;
    throw new DeliveryApiError(response.status, message, body && body.code ? String(body.code) : null);
  }
  return body;
}

const base = (siteId: string) => `/api/sites/${siteId}/bucket-delivery`;

export const bucketDeliveryApi = {
  get: (siteId: string, signal?: AbortSignal) => call(base(siteId), {}, signal),
  enable: (siteId: string, body: EnableRequest = { mode: 'hosted' }) =>
    call(`${base(siteId)}/enable`, { method: 'POST', body: JSON.stringify(body) }),
  // Phase 2.1 (#7015): a client destination is prepared, then checked and enabled; it never changes in
  // place (PUT /client-destination was removed): Disable, then prepare again with the edited values.
  prepare: (siteId: string, body: PrepareRequest) =>
    call(`${base(siteId)}/prepare`, { method: 'POST', body: JSON.stringify(body) }),
  checkAndEnable: (siteId: string) => call(`${base(siteId)}/check-and-enable`, { method: 'POST' }),
  checkNow: (siteId: string) => call(`${base(siteId)}/check-now`, { method: 'POST' }),
  detachSubscription: (siteId: string, subscription: string) =>
    call(`${base(siteId)}/subscriptions/detach`, { method: 'POST', body: JSON.stringify({ subscription }) }),
  disable: (siteId: string) => call(`${base(siteId)}/disable`, { method: 'POST' }),
  runFullSync: (siteId: string) => call(`${base(siteId)}/full-sync`, { method: 'POST' }),
  addAccount: (siteId: string, principal: string) =>
    call(`${base(siteId)}/accounts`, { method: 'POST', body: JSON.stringify({ principal }) }),
  removeAccount: (siteId: string, principal: string) =>
    call(`${base(siteId)}/accounts/${encodeURIComponent(principal)}`, { method: 'DELETE' }),
};
